import { ShipOrder } from '../ShipOrder';

import { PlanningStatus } from './PlanningStatus';
import { ValidShipOrderLineItem } from './ValidShipOrderLineItem';
import { ValidShipOrderShippingPoint } from './ValidShipOrderShippingPoint';

/**
 * The type for constructing a `ValidShipOrder`
 *
 * Equivalent to `ValidShipOrder` except for the `origin` and `destination`
 * fields, which can be normal `ShipOrderShippingPoint`s instead of the subclass
 * `ValidShipOrderShippingPoint`
 */
export type ConstructableValidShipOrder = Partial<
  Omit<ValidShipOrder, 'origin' | 'destination' | 'stops'>
> &
  Partial<Pick<ShipOrder, 'origin' | 'destination'>>;

export class ValidShipOrder extends ShipOrder {
  id: number;
  origin: ValidShipOrderShippingPoint;
  destination: ValidShipOrderShippingPoint;

  constructor(opts?: ConstructableValidShipOrder) {
    super(opts);

    if (opts?.id) {
      this.id = opts.id;
    } else {
      throw new Error('Invalid ShipOrder - id is required');
    }

    this.origin = new ValidShipOrderShippingPoint(opts?.origin);
    this.destination = new ValidShipOrderShippingPoint(opts?.destination);

    if (this.lineItems.length < 1) {
      throw new Error('Invalid ShipOrder - at least 1 LineItem required');
    }
  }

  static isValidShipOrder(shipOrder: ShipOrder): shipOrder is ValidShipOrder {
    return (
      !!shipOrder.id &&
      shipOrder.lineItems.every(ValidShipOrderLineItem.isValidShipOrderLineItem)
    );
  }

  get planningStatus(): PlanningStatus {
    const originStatus = this.origin.planningStatus;
    const destinationStatus = this.destination.planningStatus;

    return originStatus === destinationStatus
      ? originStatus
      : PlanningStatus.PARTIALLY_PLANNED;
  }

  get stops(): [
    origin: ValidShipOrderShippingPoint,
    destination: ValidShipOrderShippingPoint,
  ] {
    return [this.origin, this.destination];
  }
}
